import qs from 'query-string'
import upperFirst from 'lodash/upperFirst'
import { DateTime } from 'luxon'
import { currency } from 'lib/utils/string'
import { isOneTimePayment } from 'utils/groupEventUtils'

export const COACH_BOOK_STUDENT_PATH = '/pga-coach/book-student'
export const COACH_SCHEDULE_PATH = '/pga-coach/bookings/schedule'
export const COACH_FAST_BOOK_OPEN_PARAM = 'bookStudent'
export const COACH_FAST_BOOK_STEP_PARAM = 'bookStudentStep'
export const COACH_FAST_BOOK_SUCCESS_STEP = 'success'
export const COACH_FAST_BOOK_SUCCESS_STATE_KEY = 'coachFastBookSuccess'
export const COACH_FAST_BOOK_PAYMENT_LINK_FAILED_MESSAGE =
  "Booked, but the payment link didn't send. You can request payment from the booking details."

const COACH_BOOK_STUDENT_QUERY_KEYS = [
  COACH_FAST_BOOK_STEP_PARAM,
  'studentId',
  'offering',
  'lessonTypeId',
  'locationId',
  'startDateTime',
  'eventSlug',
  'lessonCreditBalanceQuantity',
]

export const OFFERING_TYPES = {
  PRIVATE: 'private',
  EVENT: 'event',
}

export const buildCoachBookStudentPath = (params = {}) => {
  const search = qs.stringify(params, {
    skipNull: true,
    skipEmptyString: true,
  })
  return search
    ? `${COACH_BOOK_STUDENT_PATH}?${search}`
    : COACH_BOOK_STUDENT_PATH
}

export const parseCoachBookStudentSearch = search => {
  const parsed = qs.parse(search, { parseNumbers: false })
  delete parsed[COACH_FAST_BOOK_OPEN_PARAM]
  return parsed
}

export const isCoachFastBookOverlayOpen = search => {
  const parsed = qs.parse(search, { parseNumbers: false })
  const value = parsed[COACH_FAST_BOOK_OPEN_PARAM]
  return value === '1' || value === 'true' || value === true
}

export const isCoachBookStudentSuccessStep = (params = {}) =>
  params[COACH_FAST_BOOK_STEP_PARAM] === COACH_FAST_BOOK_SUCCESS_STEP

export const isCoachBookStudentComposePhase = (
  params = {},
  { isFastBookOverlay = false } = {},
) => {
  if (isCoachBookStudentSuccessStep(params)) {
    return false
  }

  const { offering, eventSlug } = params

  if (offering === OFFERING_TYPES.EVENT && eventSlug) {
    return isFastBookOverlay
  }

  return true
}

export const shouldUseCoachFastBookOverlayUrlSync = (
  pathname,
  isFastBookEnabled,
) =>
  Boolean(isFastBookEnabled && pathname && pathname !== COACH_BOOK_STUDENT_PATH)

const normalizeSearchString = search => {
  if (!search) return ''
  return search.startsWith('?') ? search.slice(1) : search
}

export const areCoachFastBookOverlaySearchesEqual = (left, right) => {
  const leftParsed = qs.parse(normalizeSearchString(left), {
    parseNumbers: false,
  })
  const rightParsed = qs.parse(normalizeSearchString(right), {
    parseNumbers: false,
  })
  const keys = new Set([
    ...Object.keys(leftParsed),
    ...Object.keys(rightParsed),
  ])

  return [...keys].every(key => {
    const leftValue = leftParsed[key]
    const rightValue = rightParsed[key]
    const leftNormalized =
      leftValue === '' || leftValue == null ? undefined : String(leftValue)
    const rightNormalized =
      rightValue === '' || rightValue == null ? undefined : String(rightValue)
    return leftNormalized === rightNormalized
  })
}

export const buildCoachFastBookOverlaySearch = (
  params = {},
  existingSearch = '',
) => {
  const existing = qs.parse(existingSearch, { parseNumbers: false })
  COACH_BOOK_STUDENT_QUERY_KEYS.forEach(key => {
    delete existing[key]
  })
  delete existing[COACH_FAST_BOOK_OPEN_PARAM]

  const merged = {
    ...existing,
    ...params,
    [COACH_FAST_BOOK_OPEN_PARAM]: '1',
  }

  return qs.stringify(merged, {
    skipNull: true,
    skipEmptyString: true,
  })
}

export const buildCoachFastBookOverlayPath = (
  pathname,
  params = {},
  existingSearch = '',
) => {
  const search = buildCoachFastBookOverlaySearch(params, existingSearch)
  return search ? `${pathname}?${search}` : pathname
}

export const stripCoachFastBookOverlayParams = search => {
  const parsed = qs.parse(search, { parseNumbers: false })
  delete parsed[COACH_FAST_BOOK_OPEN_PARAM]
  COACH_BOOK_STUDENT_QUERY_KEYS.forEach(key => {
    delete parsed[key]
  })
  return qs.stringify(parsed, {
    skipNull: true,
    skipEmptyString: true,
  })
}

export const calendarSlotStartDateTime = (day, hour = null) => {
  if (!day) {
    return null
  }

  const start =
    hour == null
      ? day.set({ hour: 9, minute: 0, second: 0, millisecond: 0 })
      : day.set({ hour, minute: 0, second: 0, millisecond: 0 })

  return start.toISO()
}

export const buildCoachFastBookUrlFromCalendarSlot = (
  pathname,
  day,
  hour = null,
  existingSearch = '',
  extraParams = {},
) => {
  const startDateTime = calendarSlotStartDateTime(day, hour)
  const params = { ...extraParams }
  if (startDateTime) {
    params.startDateTime = startDateTime
  }
  return buildCoachFastBookOverlayPath(pathname, params, existingSearch)
}

export const buildCoachFastBookSuccessLocation = ({
  pathname,
  search,
  summary,
}) => {
  const state = { [COACH_FAST_BOOK_SUCCESS_STATE_KEY]: summary }
  const successParams = {
    [COACH_FAST_BOOK_STEP_PARAM]: COACH_FAST_BOOK_SUCCESS_STEP,
  }

  if (isCoachFastBookOverlayOpen(search)) {
    return {
      pathname,
      search: `?${buildCoachFastBookOverlaySearch(successParams, search)}`,
      state,
    }
  }

  return {
    pathname: COACH_BOOK_STUDENT_PATH,
    search: `?${qs.stringify(successParams)}`,
    state,
  }
}

const formatSuccessStartDateTime = (summary, format) => {
  if (!summary?.startDateTime) return ''
  const start = DateTime.fromISO(summary.startDateTime, {
    zone: summary.timezone || undefined,
  })
  return start.isValid ? start.toFormat(format) : ''
}

export const formatCoachFastBookSuccessWhen = summary =>
  formatSuccessStartDateTime(summary, 'ccc, MMM d · h:mm a')

const formatBookingSuccessMessage = summary => {
  const { offering, studentName, title } = summary
  if (offering === OFFERING_TYPES.EVENT) {
    return `${title} for ${studentName} registered.`
  }
  const when = formatSuccessStartDateTime(summary, 'MMM d')
  return when
    ? `${title} for ${studentName} booked on ${when}.`
    : `${title} for ${studentName} booked.`
}

export const formatCoachFastBookSuccessTitle = summary => {
  if (!summary?.studentName) return 'Booking confirmed'
  return summary.offering === OFFERING_TYPES.EVENT
    ? `${summary.studentName} is registered`
    : `${summary.studentName} is booked`
}

export const formatCoachFastBookSuccessCaption = summary =>
  summary?.offering === OFFERING_TYPES.EVENT
    ? 'They’re on the roster for this event.'
    : 'This lesson is on your schedule.'

export const formatCoachFastBookSuccessDetails = summary =>
  [formatCoachFastBookSuccessWhen(summary), summary?.locationName]
    .filter(Boolean)
    .join(' · ')

export const formatCoachFastBookPaymentLinkSent = summary => {
  if (!summary?.paymentLinkSent) return ''
  return summary.paymentLinkEmail
    ? `Payment link sent to ${summary.paymentLinkEmail}`
    : 'Payment link sent'
}

// Shapes the booking summary like the schedule data the coach dashboard's
// ScheduleRow renders, so the success view can show the same booking card.
export const buildCoachFastBookSuccessBooking = summary => {
  if (!summary?.title || !summary?.startDateTime) return null
  const start = DateTime.fromISO(summary.startDateTime)
  const end = summary.endDateTime
    ? DateTime.fromISO(summary.endDateTime)
    : summary.durationMinutes
      ? start.plus({ minutes: summary.durationMinutes })
      : null
  if (!start.isValid || !end?.isValid) return null

  const isEvent = summary.offering === OFFERING_TYPES.EVENT
  const eventTitle = summary.studentName
    ? `${summary.title} with ${summary.studentName}`
    : summary.title

  return {
    __typename: isEvent ? 'GroupEvent' : 'Session',
    id: 'coach-fast-book-success',
    title: isEvent ? eventTitle : summary.title,
    startDateTime: start.toISO(),
    endDateTime: end.toISO(),
    timezone: summary.timezone || undefined,
    isCanceled: false,
    coachFacility: summary.locationName ? { name: summary.locationName } : null,
    tags: summary.tags || [],
    lessonType: summary.category ? { category: summary.category } : null,
    enrollments: summary.studentName
      ? [{ student: { firstName: summary.studentName, lastName: '' } }]
      : [],
  }
}

export const formatCoachFastBookSuccessMessage = summary => {
  const message = formatBookingSuccessMessage(summary)
  return summary.paymentLinkSent ? `${message} Payment link sent.` : message
}

// Mirrors Student#primary_contact on the backend (guardian for minors, then
// self, then any contact), which is who receives payment request emails.
export const getStudentPrimaryContactEmail = student => {
  const representatives = (student?.representatives || []).filter(
    rep => rep?.contact,
  )
  const byRelationship = type =>
    representatives.filter(rep => rep.relationshipType === type)
  const guardians = student?.isMinor ? byRelationship('PARENT') : []
  const selves = byRelationship('SELF')
  const primaryGroup = guardians.length
    ? guardians
    : selves.length
      ? selves
      : representatives
  return primaryGroup.find(rep => rep.contact.email)?.contact.email || null
}

const isPaymentAccountChargeable = paymentAccount =>
  Boolean(
    paymentAccount &&
      !paymentAccount.deletedAt &&
      paymentAccount.chargesEnabled,
  )

export const canSendCoachFastBookPaymentLink = ({
  offering,
  offeringItem,
  email,
  hasLessonCredits = false,
}) => {
  if (!offeringItem || !email || hasLessonCredits) return false
  if (
    offering !== OFFERING_TYPES.PRIVATE &&
    offering !== OFFERING_TYPES.EVENT
  ) {
    return false
  }
  if (!offeringItem.acceptsOnlinePayments || !(offeringItem.priceInCents > 0)) {
    return false
  }
  if (!isPaymentAccountChargeable(offeringItem.paymentAccount)) return false
  if (offering === OFFERING_TYPES.EVENT) {
    return (
      isOneTimePayment(offeringItem) && !(offeringItem.numberOfPayments > 1)
    )
  }
  return true
}

export const formatCoachFastBookPaymentLinkHelper = ({
  student,
  priceInCents,
}) => {
  const price = currency(priceInCents / 100)
  const firstName = upperFirst(student?.firstName?.trim()) || 'Your student'
  const recipient = student?.isMinor
    ? `${firstName}’s parent or guardian`
    : firstName
  return `${recipient} will get an email with a link to pay ${price}.`
}
